import { z } from "zod";
import { AgentProviderSchema } from "../agent/provider-manifest.js";

export const ScheduleStatusSchema = z.enum(["active", "paused", "completed"]);
export type ScheduleStatus = z.infer<typeof ScheduleStatusSchema>;

export const ScheduleCadenceSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("every"),
    everyMs: z.number().int().positive(),
  }),
  z.object({
    type: z.literal("cron"),
    expression: z.string().trim().min(1),
  }),
]);
export type ScheduleCadence = z.infer<typeof ScheduleCadenceSchema>;

export const ScheduleTargetSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("agent"),
    agentId: z.string().uuid(),
  }),
  z.object({
    type: z.literal("new-agent"),
    config: z.object({
      provider: AgentProviderSchema,
      cwd: z.string().trim().min(1),
      modeId: z.string().trim().min(1).optional(),
      model: z.string().trim().min(1).optional(),
      thinkingOptionId: z.string().trim().min(1).optional(),
      title: z.string().trim().min(1).nullable().optional(),
      approvalPolicy: z.string().trim().min(1).optional(),
      sandboxMode: z.string().trim().min(1).optional(),
      networkAccess: z.boolean().optional(),
      webSearch: z.boolean().optional(),
      extra: z
        .object({
          codex: z.record(z.unknown()).optional(),
          claude: z.record(z.unknown()).optional(),
        })
        .partial()
        .optional(),
      systemPrompt: z.string().optional(),
      mcpServers: z.record(z.unknown()).optional(),
    }),
  }),
]);
export type ScheduleTarget = z.infer<typeof ScheduleTargetSchema>;

export const ScheduleRunSchema = z.object({
  id: z.string(),
  scheduledFor: z.string(),
  startedAt: z.string(),
  endedAt: z.string().nullable(),
  status: z.enum(["running", "succeeded", "failed"]),
  agentId: z.string().uuid().nullable(),
  output: z.string().nullable(),
  error: z.string().nullable(),
});
export type ScheduleRun = z.infer<typeof ScheduleRunSchema>;

export const StoredScheduleSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  prompt: z.string().min(1),
  cadence: ScheduleCadenceSchema,
  target: ScheduleTargetSchema,
  status: ScheduleStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  nextRunAt: z.string().nullable(),
  lastRunAt: z.string().nullable(),
  pausedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  maxRuns: z.number().int().positive().nullable(),
  runs: z.array(ScheduleRunSchema),
  /**
   * Auth-server userId of the session that created this schedule. Required
   * (non-null) on the write side — the single-tenant fallback has been
   * removed. Legacy null-owner records on disk are stamped to the host
   * owner's userId by the boot-time `stampLegacyOwnerIds` migration BEFORE
   * ScheduleService loads, so this schema parses cleanly on every supported
   * boot. Used to gate every schedule RPC (list/inspect/logs/pause/resume/
   * delete) and to scope spawned scheduled agents.
   */
  ownerUserId: z.string(),
  /**
   * Display name of the creating session. Forwarded to `createAgent(...)`
   * so the spawned scheduled agent re-routes to the correct per-user
   * Claude profile dir (matches the `ownerUsername` plumbing used by
   * handoff and the user-initiated create path).
   */
  ownerUsername: z.string().nullable().default(null),
});
export type StoredSchedule = z.infer<typeof StoredScheduleSchema>;

export const ScheduleSummarySchema = StoredScheduleSchema.omit({
  runs: true,
});
export type ScheduleSummary = z.infer<typeof ScheduleSummarySchema>;

export interface CreateScheduleInput {
  name?: string | null;
  prompt: string;
  cadence: ScheduleCadence;
  target: ScheduleTarget;
  maxRuns?: number | null;
  expiresAt?: string | null;
  /**
   * Auth-server userId of the creating session. Required — sessions are
   * always account-bound after the single-tenant cut. Stamped onto the
   * stored schedule for per-user ACL gating.
   */
  ownerUserId: string;
  /** Display name of the creating user (for Claude profile dir routing). */
  ownerUsername?: string | null;
}

export interface ScheduleExecutionResult {
  agentId: string | null;
  output: string | null;
}
